import { FC, useCallback, useContext, useState } from "react";
import { AdminEnableUserResult } from "tech-health-assessment-sdk/dist";
import { SecondaryButton, WarnButton } from "../../../../../../../../common/components/buttons/buttons";
import { CalloutCard } from "../../../../../../../../common/components/callout-card/callout-card";
import { Dialog, DialogProps } from "../../../../../../../../common/components/dialog/dialog";
import { DialogCard, DialogFooter, DialogHeader, DialogTitle } from "../../../../../../../../common/components/dialog/dialog.styled";
import { LoadingSpinner } from "../../../../../../../../common/components/loading-spinner/loading-spinner";
import { SnackbarContext } from "../../../../../../../../common/components/snackbar-provider/snackbar-provider";
import { StandardDialogInnerContainer } from "../../../../../../../../common/components/standard-dialog-inner-container/standard-dialog-inner-container";
import { AppColors } from "../../../../../../../../common/constants/colors/app-colors";
import { service } from "./reset-password-dialog.service";

type ResetPasswordDialogProps = {
    userId: string
} & Omit<DialogProps, 'children'>

export const ResetPasswordDialog: FC<ResetPasswordDialogProps> = ({
    userId,
    isOpen,
    onClose,
}) => {

    // context
    const { showSnackbar } = useContext(SnackbarContext)

    // state
    const [isLoading, setIsLoading] = useState<boolean>(false)

    // event handlers
    const handleDisableUserClick = useCallback(async () => {
        setIsLoading(true)

        let result: AdminEnableUserResult | undefined = undefined;
        let error: unknown;
        try {
            result = await service.resetUserPassword(userId)
        } catch (err) {
            error = err;
        }

        if (!!error || !result?.success) {
            showSnackbar({
                type: 'error',
                title: 'Something went wrong',
                message: 'If the issue persists, please contact support.'
            });
        } else {
            showSnackbar({
                type: 'success',
                title: 'Successfully send password reset link',
            })
        }

        onClose()
        setIsLoading(false)
    }, [onClose, showSnackbar, userId])

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
        >
            <DialogCard isOpen={isOpen}>
                <StandardDialogInnerContainer>
                    <DialogHeader>
                        <DialogTitle>
                            Reset User Password?
                        </DialogTitle>
                    </DialogHeader>
                    <CalloutCard type='warning'>
                        <h2>What will happen?</h2>
                        <br />
                        <span>If you proceed...</span>
                        <ul>
                            <li>The user's current password will no longer work.</li>
                            <li>We will send a link to the user's email address that will allow them to set a new password for their account.</li>
                        </ul>
                    </CalloutCard>
                    <DialogFooter>
                        <SecondaryButton
                            disabled={isLoading}
                            onClick={onClose}
                        >
                            Cancel
                        </SecondaryButton>
                        <WarnButton
                            disabled={isLoading}
                            onClick={handleDisableUserClick}
                        >
                            {isLoading && (
                                <LoadingSpinner
                                    size={14}
                                    color={AppColors.text.onDarkBackground}
                                />
                            )}
                            Send Password Reset Link
                        </WarnButton>
                    </DialogFooter>
                </StandardDialogInnerContainer>
            </DialogCard>
        </Dialog>
    )
}